import React, {memo, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import _, {compact} from 'lodash';
import {FmlxIcon, FmlxDivider, FmlxButton, FmlxTextBox, FmlxTooltip} from 'fmlx-common-ui';
import {connect, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import styled from 'styled-components';
import {
  getSelectedProtocolAndFolderIds,
  selectProtocolExplorerActiveFilter,
  selectProtocolExplorerActiveSort,
} from 'stores/selectors/protocolExplorerSelectors';
import {Size} from 'constants/UIComponentEnum';
import {
  ProtocolExplorerAccordionGroup,
  ProtocolExplorerFilterType,
  ProtocolExplorerSortingAccessor,
} from 'constants/protocolExplorerEnum';
import {selectIsInstrumentOperational, selectIsLabwareOperational} from 'stores/selectors/instrumentSelectors';
import {DrawerButtonTitle} from '../Common';
import {HeaderLayout} from '../../components';
import withIntl from '../../lib/withIntl';
import {showLoading, hideLoading} from '../../stores/actions/appStateAction';
import {showComingSoonDialog} from '../../stores/business/commonModalBusiness';
import {ExplorerViewMode, TrayType, ExplorerContentTypes, SortDirectionType} from '../../constants/dataEnum';
import ProtocolExplorerDropDown from './ProtocolExplorerDropDown';
import MenuIcon from '../../components/MenuIcon/MenuIcon';
import {getIsOperator} from '../../stores/selectors/sessionSelectors';
import ProtocolExplorerMenuHeader from './ProtocolExplorerMenuHeader';

const ExplorerHeaderGroupMenu = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  align-items: center;
  margin: 0px 8px;
  gap: 8px;
  .fmlx-divider {
    height: 100%;
  }
`;

const ExplorerHeaderOpenButton = styled.div`
  margin-right: 8px;
`;

const ExplorerHeader = memo((props) => {
  const generateExplorerViewModeDropdown = () => {
    const {explorerViewMode, intl, explorerViewModeChange} = props;

    const detailViewLabel = intl.formatMessage({id: 'explorer.explorerHeader.detailView'});
    const listViewLabel = intl.formatMessage({id: 'explorer.explorerHeader.listView'});
    const thumbnailLabel = intl.formatMessage({id: 'explorer.explorerHeader.thumbnail'});
    const detailViewIcon = 'DetailsList';
    const listViewIcon = 'ViewList';
    const thumbnailIcon = 'Thumbnail';
    let mainLabel = '';
    let mainIcon = '';

    switch (explorerViewMode) {
      case ExplorerViewMode.DETAIL:
        mainLabel = detailViewLabel;
        mainIcon = detailViewIcon;

        break;
      case ExplorerViewMode.LIST:
        mainLabel = listViewLabel;
        mainIcon = listViewIcon;

        break;
      case ExplorerViewMode.THUMBNAIL:
      default:
        mainLabel = thumbnailLabel;
        mainIcon = thumbnailIcon;

        break;
    }

    return (
      <ProtocolExplorerDropDown
        menuLabel={mainLabel}
        menuIcon={mainIcon}
        menuList={[
          {
            title: thumbnailLabel,
            disabled: explorerViewMode === ExplorerViewMode.THUMBNAIL,
            onClick: () => explorerViewModeChange(ExplorerViewMode.THUMBNAIL),
            leftIcon: <FmlxIcon customColor="black" name={thumbnailIcon} />,
          },
          {
            title: listViewLabel,
            disabled: explorerViewMode === ExplorerViewMode.LIST,
            onClick: () => explorerViewModeChange(ExplorerViewMode.LIST),
            leftIcon: <FmlxIcon customColor="black" name={listViewIcon} />,
          },
          {
            title: detailViewLabel,
            disabled: explorerViewMode === ExplorerViewMode.DETAIL,
            onClick: () => {
              explorerViewModeChange(ExplorerViewMode.DETAIL);
            },
            leftIcon: <FmlxIcon customColor="black" name={detailViewIcon} />,
          },
        ]}
      />
    );
  };

  const {
    intl,
    onSortProtocol,
    onFilterProtocolByName,
    protocolList,
    explorerViewMode,
    addProtocolClick,
    addFolderClick,
    removeProtocolClick,
    renameProtocolClick,
    downloadProtocolClick,
    uploadProtocolClick,
    onFilterProtocolByTrayID,
    onFilterTessaraProtocol,
    onFilterProtocolByTrayType,
    onFilterByShareProtocolAndFolder,
    onShowAllProtocol,
    onOpenButtonClick,
    onShareProtocolHandler,
    onCutClick,
    onCopyClick,
    onPasteClick,
    onRefreshClick,
    filterByTrayDisabled,
    runningProtocol,
    clipboardProtocolIds,
    isTessaraSuperUser,
    isTessaraProtocol,
    hasTessaraLicense,
    isMultiTypeSelectedProtocols,
    convertToTessaraProtocol,
    protocolFolderList,
    currentProtocolFolder,
    userState,
    isSameListofUserForProtocols,
    isProtocolOwner,
    canUserPasteProtocolsFolders,
  } = props;
  const isOperatorRoleLoggedIn = useSelector(getIsOperator);
  const selectedProtocolAndFolderIds = useSelector(getSelectedProtocolAndFolderIds);
  const activeFilter = useSelector(selectProtocolExplorerActiveFilter);
  const activeSort = useSelector(selectProtocolExplorerActiveSort);
  const isInstrumentOperational = useSelector(selectIsInstrumentOperational);
  const isLabwareOperational = useSelector(selectIsLabwareOperational);

  const nameFilterValue = useMemo(
    () => (activeFilter.type === ProtocolExplorerFilterType.NAME ? activeFilter.value : ''),
    [activeFilter.type, activeFilter.value],
  );

  const runningProtocolId = runningProtocol?.id || null;
  const runningFolderIds = runningProtocol?.protocolFolderLineages?.map((e) => e.id) || Array(0);
  const isSharedFolder = currentProtocolFolder && currentProtocolFolder?.owner !== userState?.userId;
  const isNotAllowedToCreateOrImport = isOperatorRoleLoggedIn || isSharedFolder;

  const selectedProtocols = useMemo(
    () => protocolList.filter((e) => e.isSelected || selectedProtocolAndFolderIds.includes(e.ID)),
    [protocolList, selectedProtocolAndFolderIds],
  );
  const selectedProtocolIds = selectedProtocols.map((e) => e.ID);
  const selectedProtocolsCount = selectedProtocols.length;
  const isThereSelectedProtocol = selectedProtocolsCount > 0;
  const isOneProtocolSelected = selectedProtocols.length === 1;

  const isContainingFavoriteGroupItem = selectedProtocols.find(
    (item) => item.groupView === ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS,
  );

  const selectedFolders = selectedProtocols.filter((p) => p.type === ExplorerContentTypes.FOLDER);
  const isSelectingFolders = selectedFolders.length > 0;

  // only sorts based on name. other than name, reset to ascending by default
  const sortStatus =
    activeSort.accessor === ProtocolExplorerSortingAccessor.NAME ? activeSort.direction : SortDirectionType.ASC;
  const glyphIconSort = sortStatus === SortDirectionType.ASC ? 'SortAscendingAlpha' : 'SortDescendingAlpha';

  const fileUploadInputRef = useRef(null);

  const handleImportClick = () => {
    fileUploadInputRef.current.value = '';
    fileUploadInputRef.current.click();
  };

  // TODO : need to refactor for disabled menu (because it should be the same with right clicked behavior).
  const moreDropDownMenuList = [
    {
      id: 'rename',
      label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.renameOption'}),
      disabled:
        selectedProtocolsCount !== 1 ||
        selectedProtocolIds.includes(runningProtocolId) ||
        selectedProtocolIds.some((e) => runningFolderIds.includes(e)) ||
        (!isTessaraSuperUser && isTessaraProtocol) ||
        !isProtocolOwner,
      onClick: renameProtocolClick,
      icon: <FmlxIcon customColor="black" size={Size.SM} name="Rename" />,
    },
    {
      id: 'sendCopy',
      label: intl.formatMessage({id: 'explorer.explorerHeader.menuShare'}),
      disabled: !isThereSelectedProtocol || isTessaraProtocol || isMultiTypeSelectedProtocols || !isProtocolOwner,
      onClick: () => onShareProtocolHandler(),
      icon: <FmlxIcon customColor="black" size={Size.SM} name="SendACopy" />,
    },
    {
      id: 'sendCopyRunOnly',
      label: intl.formatMessage({id: 'explorer.explorerHeader.menuShare.context'}),
      disabled:
        !isThereSelectedProtocol ||
        !isSameListofUserForProtocols ||
        selectedProtocolIds.includes(runningProtocolId) ||
        runningFolderIds.some((e) => selectedProtocolIds.includes(e)) ||
        !isProtocolOwner,
      onClick: () => onShareProtocolHandler(true),
      icon: <FmlxIcon customColor="black" size={Size.SM} name="FastFolderShared" />,
    },
    {
      id: 'cut',
      label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.cutProtocolFile'}),
      disabled:
        !isThereSelectedProtocol ||
        selectedProtocolIds.includes(runningProtocolId) ||
        isContainingFavoriteGroupItem ||
        selectedProtocolIds.some((e) => runningFolderIds.includes(e)) ||
        (!isTessaraProtocol && !isProtocolOwner),
      onClick: onCutClick,
      icon: <FmlxIcon customColor="black" size={Size.SM} name="Cut" />,
    },
    {
      id: 'copy',
      label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.copyProtocolFile'}),
      disabled: !isThereSelectedProtocol || (!isTessaraSuperUser && isTessaraProtocol) || !isProtocolOwner,
      onClick: onCopyClick,
      icon: <FmlxIcon customColor="black" size={Size.SM} name="FileCopy" />,
    },
    {
      id: 'paste',
      label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.pasteProtocolFile'}),
      disabled: !clipboardProtocolIds || !clipboardProtocolIds.length || !canUserPasteProtocolsFolders,
      onClick: onPasteClick,
      icon: <FmlxIcon customColor="black" size={Size.SM} name="FilePaste" />,
    },
    isTessaraSuperUser && {
      id: 'convert',
      disabled:
        !isThereSelectedProtocol ||
        selectedProtocolIds.includes(runningProtocolId) ||
        isMultiTypeSelectedProtocols ||
        isSelectingFolders,
      label: intl.formatMessage({
        id: `explorer.explorerHeader.menuMore.${isTessaraProtocol ? 'convertToRegularProtocol' : 'convertToTessaraProtocol'}.context`,
      }),
      onClick: convertToTessaraProtocol,
      icon: <FmlxIcon customColor="black" size={Size.SM} name={isTessaraProtocol ? 'Protocol' : 'FastTessara'} />,
    },
    {
      id: 'import',
      label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.uploadOption'}),
      onClick: handleImportClick,
      disabled: isNotAllowedToCreateOrImport || !isInstrumentOperational || !isLabwareOperational,
      icon: <FmlxIcon customColor="black" size={Size.SM} name="Upload" />,
      htmlFor:
        isNotAllowedToCreateOrImport || !isInstrumentOperational || !isLabwareOperational ? '' : 'upload-protocol',
      tooltip: {
        title:
          !isInstrumentOperational || !isLabwareOperational
            ? intl.formatMessage({
                id: `explorer.importProtocol.disabled.${!isInstrumentOperational ? 'instrument' : 'labware'}`,
              })
            : '',
        id: 'import-disabled-tooltip',
        placement: FmlxTooltip.Placement.LEFT,
      },
    },
    {
      id: 'download',
      label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.downloadOption'}),
      disabled:
        !isThereSelectedProtocol ||
        (!isTessaraSuperUser && (isMultiTypeSelectedProtocols || isTessaraProtocol)) ||
        !isProtocolOwner,
      onClick: downloadProtocolClick,
      icon: <FmlxIcon customColor="black" size={Size.SM} name="Download" />,
    },
    {
      id: 'remove',
      disabled:
        !isThereSelectedProtocol ||
        (!isTessaraSuperUser && (isMultiTypeSelectedProtocols || isTessaraProtocol)) ||
        selectedProtocolIds.includes(runningProtocolId) ||
        !isProtocolOwner,
      label: intl.formatMessage({id: 'explorer.explorerHeader.menuRemove'}),
      onClick: removeProtocolClick,
      icon: <FmlxIcon customColor="black" size={Size.SM} name="Trash" />,
    },
  ];

  const newDropDownMenuList = [
    {
      title: intl.formatMessage({
        id: `explorer.explorerHeader.${isTessaraSuperUser ? 'menuRegularProtocol' : 'menuProtocol'}`,
      }),
      leftIcon: <FmlxIcon customColor="black" name="Protocol" />,
      onClick: () => addProtocolClick(false),
    },
    {
      title: intl.formatMessage({
        id: 'explorer.explorerHeader.menuFolder',
      }),
      leftIcon: <FmlxIcon customColor="black" name="Folder" />,
      onClick: addFolderClick,
      disabled: protocolFolderList.length >= 5,
    },
  ];

  if (isTessaraSuperUser) {
    newDropDownMenuList.splice(1, 0, {
      title: intl.formatMessage({
        id: 'explorer.explorerHeader.menuTessaraProtocol',
      }),
      leftIcon: <FmlxIcon customColor="black" name="FastTessaraProtocol" />,
      onClick: () => addProtocolClick(true),
    });
  }

  const handleSearchBarChange = (input) => {
    const {value} = input;
    onFilterProtocolByName(value);
  };

  return (
    <HeaderLayout>
      <HeaderLayout.Left>
        <DrawerButtonTitle title={intl.formatMessage({id: 'explorer.explorerHeader.titleMenu'})} />
      </HeaderLayout.Left>
      <HeaderLayout.Center>
        <div className="override-bootstrap-iso-fieldset">
          <FmlxTextBox
            size="md"
            placeholder={intl.formatMessage({id: 'explorer.explorerHeader.menuSearch'})}
            value={nameFilterValue}
            onChange={handleSearchBarChange}
          />
        </div>
      </HeaderLayout.Center>
      <HeaderLayout.Right>
        <ExplorerHeaderGroupMenu>
          <MenuIcon label="Refresh" icon="Refresh" dropDownIcon={null} onClick={onRefreshClick} />
          <ProtocolExplorerDropDown
            menuLabel={intl.formatMessage({id: 'explorer.explorerHeader.menuNew'})}
            menuIcon="AddCircle"
            menuList={newDropDownMenuList}
            hidden={isNotAllowedToCreateOrImport}
          />
          <FmlxDivider orientation="vertical" />
          <ProtocolExplorerDropDown
            menuLabel={intl.formatMessage({id: 'explorer.explorerHeader.menuFliterBy'})}
            menuIcon="FilterAlt"
            menuList={_.compact([
              {
                title: intl.formatMessage({
                  id: 'explorer.explorerHeader.menuFliterBy.showAllProtocolsOption',
                }),
                onClick: onShowAllProtocol,
                leftIcon: <FmlxIcon customColor="black" name="Protocol" />,
              },
              {
                title: (
                  <>
                    <div>
                      {intl.formatMessage({
                        id: 'explorer.explorerHeader.menuFliterBy.showSavedInCurrentTrayOption',
                      })}
                    </div>
                    <div className="filter-by-tray">{filterByTrayDisabled ? '(No Tray Inserted)' : ''}</div>
                  </>
                ),
                disabled: filterByTrayDisabled,
                onClick: onFilterProtocolByTrayID,
                leftIcon: <FmlxIcon customColor="black" name="Save" />,
              },
              hasTessaraLicense && {
                title: intl.formatMessage({
                  id: 'explorer.explorerHeader.menuFliterBy.showTessaraProtocols',
                }),
                onClick: onFilterTessaraProtocol,
                leftIcon: <FmlxIcon customColor="black" name="FastTessaraProtocol" size="xs" />,
              },
              {
                title: intl.formatMessage({
                  id: 'explorer.explorerHeader.menuFliterBy.showNormalCaddyProtocols',
                }),
                onClick: () => onFilterProtocolByTrayType(TrayType.NORMAL_CADDY),
                leftIcon: <FmlxIcon customColor="black" name="FastPlateVertical" size="xs" />,
              },
              {
                title: intl.formatMessage({
                  id: 'explorer.explorerHeader.menuFliterBy.showDoubleCaddyProtocols',
                }),
                onClick: () => onFilterProtocolByTrayType(TrayType.DOUBLE_CADDY),
                leftIcon: <FmlxIcon customColor="black" name="FastDoublePlate" />,
              },
              {
                title: intl.formatMessage({
                  id: 'explorer.explorerHeader.menuFliterBy.showHighVolumeCaddyProtocols',
                }),
                onClick: () => onFilterProtocolByTrayType(TrayType.HIGH_VOLUME_CADDY),
                leftIcon: <FmlxIcon customColor="black" name="MantisPipette" />,
              },
              !isOperatorRoleLoggedIn && {
                title: intl.formatMessage({
                  id: 'explorer.explorerHeader.menuFliterBy.shareProtocolAndFolder',
                }),
                onClick: onFilterByShareProtocolAndFolder,
                leftIcon: <FmlxIcon customColor="black" name="FastFolderShared" />,
              },
            ])}
          />
          {generateExplorerViewModeDropdown()}
          <ProtocolExplorerDropDown
            menuLabel={intl.formatMessage(
              {id: 'explorer.explorerHeader.menuSortName'},
              {sortStatus: sortStatus === 'asc' ? 'Asc' : 'Des'},
            )}
            menuIcon={glyphIconSort}
            disabled={explorerViewMode === ExplorerViewMode.DETAIL}
            onClick={onSortProtocol}
          />
          {isOperatorRoleLoggedIn ? '' : <FmlxDivider orientation="vertical" />}
          <ProtocolExplorerMenuHeader
            menuLabel={intl.formatMessage({id: 'explorer.explorerHeader.menuMore'})}
            menuIcon="DotsVertical"
            dropDownIcon={false}
            menuList={compact(moreDropDownMenuList)}
            hidden={isOperatorRoleLoggedIn}
          />
          <input
            ref={fileUploadInputRef}
            type="file"
            accept=".zip"
            multiple
            id="upload-protocol"
            onChange={uploadProtocolClick}
            hidden
            style={{display: 'none'}}
          />
          <FmlxDivider orientation="vertical" />
        </ExplorerHeaderGroupMenu>
        <ExplorerHeaderOpenButton>
          <FmlxButton
            disabled={!isOneProtocolSelected}
            id="run"
            size="xl"
            type="primary"
            label={intl.formatMessage({id: 'explorer.explorerHeader.openButton'})}
            onClick={onOpenButtonClick}
          />
        </ExplorerHeaderOpenButton>
      </HeaderLayout.Right>
    </HeaderLayout>
  );
});

function mapStateToProps(state) {
  const {protocolClipboardEventType, protocolClipboardItems} = state.protocolExplorerState;

  return {
    trayID: state.trayCalibrationState.id,
    runningProtocol: state.protocolExplorerState.runningProtocol,
    clipboardProtocolIds: protocolClipboardEventType ? protocolClipboardItems.map((e) => e.ID) : [],
    protocolFolderList: state.protocolExplorerState.protocolFolderList,
    currentProtocolFolder: state.protocolExplorerState.currentProtocolFolder,
    userState: state.sessionState.user,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onShowComingSoonDialog: bindActionCreators(showComingSoonDialog, dispatch),
    onShowLoading: bindActionCreators(showLoading, dispatch),
    onHideLoading: bindActionCreators(hideLoading, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(ExplorerHeader));

ExplorerHeader.propTypes = {
  intl: PropTypes.any.isRequired,
  onSortProtocol: PropTypes.func.isRequired,
  onFilterProtocolByName: PropTypes.func.isRequired,
  protocolList: PropTypes.array.isRequired,
  onShowAllProtocol: PropTypes.func.isRequired,
  onFilterProtocolByTrayID: PropTypes.func.isRequired,
  onFilterTessaraProtocol: PropTypes.func.isRequired,
  onFilterProtocolByTrayType: PropTypes.func.isRequired,
  onFilterByShareProtocolAndFolder: PropTypes.func.isRequired,
  filterByTrayDisabled: PropTypes.bool,
  removeProtocolClick: PropTypes.func,
  renameProtocolClick: PropTypes.func,
  downloadProtocolClick: PropTypes.func,
  uploadProtocolClick: PropTypes.func,
  onOpenButtonClick: PropTypes.func,
  addProtocolClick: PropTypes.func.isRequired,
  onShareProtocolHandler: PropTypes.func.isRequired,
  onRefreshClick: PropTypes.func.isRequired,
  explorerViewMode: PropTypes.number,
  explorerViewModeChange: PropTypes.func,
  isTessaraSuperUser: PropTypes.bool.isRequired,
  isTessaraProtocol: PropTypes.bool.isRequired,
  hasTessaraLicense: PropTypes.bool.isRequired,
  isMultiTypeSelectedProtocols: PropTypes.bool.isRequired,
  convertToTessaraProtocol: PropTypes.func.isRequired,
  addFolderClick: PropTypes.func.isRequired,
  onCutClick: PropTypes.func,
  onCopyClick: PropTypes.func,
  onPasteClick: PropTypes.func,
  runningProtocol: PropTypes.any,
  clipboardProtocolIds: PropTypes.array,
  protocolFolderList: PropTypes.any,
  currentProtocolFolder: PropTypes.any,
  userState: PropTypes.any,
  isSameListofUserForProtocols: PropTypes.bool,
  isProtocolOwner: PropTypes.bool,
  canUserPasteProtocolsFolders: PropTypes.bool,
};

ExplorerHeader.defaultProps = {
  filterByTrayDisabled: true,
  explorerViewMode: ExplorerViewMode.THUMBNAIL,
  removeProtocolClick: () => {},
  renameProtocolClick: () => {},
  downloadProtocolClick: () => {},
  uploadProtocolClick: () => {},
  onOpenButtonClick: () => {},
  explorerViewModeChange: () => {},
  onCutClick: () => {},
  onCopyClick: () => {},
  onPasteClick: () => {},
  runningProtocol: null,
  clipboardProtocolIds: [],
  protocolFolderList: null,
  currentProtocolFolder: null,
  userState: null,
  isSameListofUserForProtocols: false,
  isProtocolOwner: false,
  canUserPasteProtocolsFolders: true,
};
